/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER, TIPOS_ACTIVO, DESTINO_TIPOS, getVidaUtilPorTipo } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { crearActivo } from "../../utils/activos";
import { vincularFacturaRecibida, ignorarFacturaRecibida, reactivarFactura } from "../../utils/conciliacionSII";
import { actualizarProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto) => {
  if (monto == null) return "-";
  return `$ ${Number(monto).toLocaleString("es-CL")}`;
};

const formatRut = (rut) => {
  if (!rut) return "-";
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

// Agrupar CUENTAS_ER por grupo
const CUENTAS_POR_GRUPO = {};
CUENTAS_ER.forEach((c) => {
  if (!CUENTAS_POR_GRUPO[c.grupo]) CUENTAS_POR_GRUPO[c.grupo] = [];
  CUENTAS_POR_GRUPO[c.grupo].push(c);
});

const LINEA_VACIA = {
  tipo_activo: "",
  descripcion: "",
  numero_serie: "",
  cantidad: 1,
  costo_unitario: 0,
  destino_tipo: "",
  destino_id: "",
};

// ─── Componente Modal ────────────────────────────────────

const AsignarRecibidaModal = ({ isOpen, toggle, factura, onAsignada }) => {
  const { pais, id: userId } = useAuthContext();

  const [cuentaER, setCuentaER] = useState("");
  const [tipoContable, setTipoContable] = useState("");
  const [recordar, setRecordar] = useState(false);
  const [proveedor, setProveedor] = useState(null);
  const [facturaProveedor, setFacturaProveedor] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [plazopago, setPlazopago] = useState("");

  // Líneas de activo
  const [lineasActivo, setLineasActivo] = useState([]);
  const [lineasExpandidas, setLineasExpandidas] = useState({});
  const [edificios, setEdificios] = useState([]);
  const [proyectosCarteleria, setProyectosCarteleria] = useState([]);

  // Cargar proveedor y factura_proveedor existente
  const cargarContexto = useCallback(async () => {
    if (!factura || !pais) return;
    setCargando(true);
    try {
      // Buscar proveedor por rut_ruc
      const { data: proveedores } = await client
        .from("proveedores")
        .select("id, nombre, rut_ruc, cuenta_er_default, tipo_contable_default")
        .eq("rut_ruc", factura.rut_emisor)
        .eq("pais", pais)
        .limit(1);

      const prov = proveedores?.[0] || null;
      setProveedor(prov);

      // Pre-seleccionar cuenta_er_default del proveedor
      if (prov?.cuenta_er_default) {
        setCuentaER(prov.cuenta_er_default);
      }
      if (prov?.tipo_contable_default) {
        setTipoContable(prov.tipo_contable_default);
      }

      // Buscar factura_proveedor existente con mismo folio
      if (prov) {
        const { data: facturas } = await client
          .from("facturas_proveedor")
          .select("id, monto_total, fecha, estado, numero_factura")
          .eq("proveedor_id", prov.id)
          .eq("numero_factura", String(factura.folio))
          .limit(1);

        setFacturaProveedor(facturas?.[0] || null);
      }

      // Cargar edificios y proyectos cartelería para selectores de destino
      const [{ data: edifData }, { data: cartData }] = await Promise.all([
        client.from("edificios").select("id, edificio").eq("pais", pais).order("edificio"),
        client
          .from("proyectos_carteleria")
          .select("id, nombre_proyecto, referencia_id")
          .eq("pais", pais)
          .order("nombre_proyecto"),
      ]);
      setEdificios(edifData || []);
      setProyectosCarteleria(cartData || []);
    } catch (err) {
      console.error("Error cargando contexto:", err);
    } finally {
      setCargando(false);
    }
  }, [factura, pais]);

  useEffect(() => {
    if (isOpen && factura) {
      setCuentaER(factura.cuenta_er || "");
      setTipoContable(factura.tipo_contable || "");
      setRecordar(false);
      setConfirmando(false);
      setModoEdicion(false);
      setProveedor(null);
      setFacturaProveedor(null);
      setLineasActivo([]);
      setPlazopago(factura.plazopago || "");
      cargarContexto();
    }
  }, [isOpen, factura, cargarContexto]);

  // Derivar categoría ER del valor seleccionado
  const getCategoriaER = (valor) => {
    const cuenta = CUENTAS_ER.find((c) => c.value === valor);
    if (!cuenta) return null;
    if (cuenta.grupo === "Costos Fijos") return "costo_fijo";
    if (cuenta.grupo === "Costos Variables") return "costo_variable";
    return "otro";
  };

  // ─── Helpers líneas de activo ──────────────────────────

  const agregarLinea = () => setLineasActivo([...lineasActivo, { ...LINEA_VACIA }]);

  const eliminarLinea = (idx) => setLineasActivo(lineasActivo.filter((_, i) => i !== idx));

  const actualizarLinea = (idx, campo, valor) => {
    setLineasActivo((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        const updated = { ...l, [campo]: valor };
        // Al cambiar destino_tipo, resetear destino_id
        if (campo === "destino_tipo") updated.destino_id = "";
        return updated;
      })
    );
  };

  // ─── Totalizador ──────────────────────────────────────

  const totalLineas = lineasActivo.reduce(
    (sum, l) => sum + (Number(l.cantidad) || 0) * (Number(l.costo_unitario) || 0),
    0
  );
  const montoNeto = Number(factura?.monto_neto) || 0;
  const diferenciaOk = lineasActivo.length > 0 && Math.abs(totalLineas - montoNeto) < 1;

  // ─── Validación líneas ────────────────────────────────

  const validarLineas = () => {
    if (lineasActivo.length === 0) {
      toast.error("Agregá al menos una línea de activo");
      return false;
    }
    for (let i = 0; i < lineasActivo.length; i++) {
      const l = lineasActivo[i];
      if (!l.tipo_activo) {
        toast.error(`Línea ${i + 1}: seleccioná un tipo de activo`);
        return false;
      }
      if (!l.cantidad || l.cantidad < 1) {
        toast.error(`Línea ${i + 1}: cantidad debe ser al menos 1`);
        return false;
      }
      if (!l.costo_unitario || l.costo_unitario <= 0) {
        toast.error(`Línea ${i + 1}: costo unitario debe ser mayor a 0`);
        return false;
      }
    }
    return true;
  };

  // ─── Asignar ──────────────────────────────────────────

  const handleAsignar = async () => {
    if (tipoContable === "gasto" && !cuentaER) {
      toast.error("Seleccioná una cuenta del ER");
      return;
    }
    if (tipoContable === "activo" && !validarLineas()) return;
    if (!plazopago) {
      toast.error("Ingresá una fecha de pago estimada para continuar");
      return;
    }

    const moneda = pais === "Chile" ? "CLP" : "PEN";

    setGuardando(true);
    try {
      // 1. Vincular la factura SII recibida
      await vincularFacturaRecibida(factura.id, {
        proveedor_id: proveedor?.id || factura.proveedor_id || null,
        factura_proveedor_id: facturaProveedor?.id || null,
        cuenta_er: cuentaER,
        cuenta_er_categoria: getCategoriaER(cuentaER),
        tipo_contable: tipoContable,
        usuario_id: userId,
      });

      // Persistir plazopago: factura_proveedor (si existe) + SII recibida
      if (facturaProveedor?.id) {
        await client.from("facturas_proveedor").update({ fecha_vencimiento: plazopago }).eq("id", facturaProveedor.id);
      }
      await client.from("facturas_sii_recibidas").update({ plazopago }).eq("id", factura.id);

      // 2. Si es activo, crear activos
      if (tipoContable === "activo" && lineasActivo.length > 0) {
        for (const l of lineasActivo) {
          // eslint-disable-next-line no-await-in-loop
          await crearActivo({
            pais,
            fecha_compra: factura.fecha_emision?.substring(0, 10) || new Date().toISOString().substring(0, 10),
            tipo_activo: l.tipo_activo,
            descripcion: l.descripcion || factura.razon_social_emisor,
            numero_serie: l.numero_serie || null,
            numero_factura: String(factura.folio),
            cantidad: Number(l.cantidad) || 1,
            costo_unitario: Number(l.costo_unitario) || 0,
            moneda,
            vida_util_meses: getVidaUtilPorTipo(l.tipo_activo),
            valor_residual: 0,
            estado: "activo",
            destino_tipo: l.destino_tipo || null,
            destino_id: l.destino_id || null,
            proveedor_id: proveedor?.id || null,
            factura_sii_recibida_id: factura.id,
          });
        }
      }

      // 3. Recordar cuenta_er_default para el proveedor
      if (recordar && proveedor) {
        await actualizarProveedor(proveedor.id, {
          cuenta_er_default: cuentaER,
          tipo_contable_default: tipoContable,
        });
      }

      toast.success("Factura asignada correctamente");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error asignando:", err);
      toast.error("Error al asignar factura");
    } finally {
      setGuardando(false);
    }
  };

  const handleIgnorar = async () => {
    setGuardando(true);
    try {
      await ignorarFacturaRecibida(factura.id, userId);
      toast.success("DTE marcado como ignorado");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error ignorando:", err);
      toast.error("Error al ignorar DTE");
    } finally {
      setGuardando(false);
    }
  };

  const handleReactivar = async () => {
    setGuardando(true);
    try {
      await reactivarFactura(factura.id, "recibida");
      toast.success("Factura reactivada");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error reactivando:", err);
      toast.error("Error al reactivar factura");
    } finally {
      setGuardando(false);
    }
  };

  if (!factura) return null;

  const esIgnorada = factura.estado_vinculacion === "ignorada";
  const cuentaLabel = CUENTAS_ER.find((c) => c.value === cuentaER)?.label;
  const tieneDefaults = proveedor?.cuenta_er_default && proveedor?.tipo_contable_default;
  const modoRapido = tieneDefaults && !modoEdicion;
  const defaultCuentaLabel =
    CUENTAS_ER.find((c) => c.value === proveedor?.cuenta_er_default)?.label || proveedor?.cuenta_er_default;

  // ─── Opciones destino por línea ───────────────────────

  const getDestinoOpciones = (destTipo) => {
    if (destTipo === "edificio") return edificios.map((e) => ({ value: e.id, label: e.edificio }));
    if (destTipo === "carteleria") {
      return proyectosCarteleria.map((p) => ({ value: p.id, label: p.nombre_proyecto || p.referencia_id }));
    }
    return [];
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Asignar — Folio {factura.folio} &middot; {factura.razon_social_emisor}
      </ModalHeader>
      <ModalBody>
        {/* Banner ignorada */}
        {esIgnorada && (
          <div
            style={{
              background: "#fff3cd",
              color: "#856404",
              padding: "10px 14px",
              borderRadius: 6,
              marginBottom: 14,
              fontSize: "0.82rem",
              fontWeight: 600,
            }}
          >
            Esta factura está marcada como descartada
          </div>
        )}

        {/* Detalle del DTE — una línea compacta */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.8rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
            alignItems: "center",
          }}
        >
          <span>
            <strong>Fecha:</strong> {formatFecha(factura.fecha_emision)}
          </span>
          <span>
            <strong>RUT:</strong> {formatRut(factura.rut_emisor)}
          </span>
          <span>
            <strong>Neto:</strong> {formatMonto(factura.monto_neto)}
          </span>
          <span>
            <strong>IVA:</strong> {formatMonto(factura.monto_iva)}
          </span>
          <span>
            <strong>Total:</strong> {formatMonto(factura.monto_total)}
          </span>
        </div>

        {!esIgnorada && cargando && (
          <div style={{ textAlign: "center", padding: 16, color: "#999" }}>Cargando datos del proveedor...</div>
        )}

        {/* Modo rápido: proveedor con cuenta recordada */}
        {!esIgnorada && !cargando && modoRapido && (
          <div
            style={{
              padding: "14px 16px",
              background: "#eafaf1",
              borderRadius: 6,
              border: "1px solid #a9dfbf",
              fontSize: "0.85rem",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <strong>{proveedor.nombre}</strong>
              {" se asigna como "}
              <strong style={{ textTransform: "capitalize" }}>{proveedor.tipo_contable_default}</strong>
              {" en "}
              <strong>{defaultCuentaLabel}</strong>
            </div>
            {facturaProveedor && (
              <div style={{ fontSize: "0.78rem", color: "#2980b9", marginBottom: 10 }}>
                Factura proveedor existente: #{facturaProveedor.numero_factura} —{" "}
                {formatMonto(facturaProveedor.monto_total)}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                Pago estimado <span style={{ color: "#e74c3c" }}>*</span>
              </label>
              <input
                type="date"
                value={plazopago}
                onChange={(e) => setPlazopago(e.target.value)}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: `1px solid ${!plazopago ? "#e74c3c" : "#aed6f1"}`,
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button color="success" size="sm" style={{ minWidth: 100 }} onClick={handleAsignar} disabled={guardando}>
                {guardando ? "Guardando..." : "Confirmar"}
              </Button>
              <Button color="warning" size="sm" style={{ minWidth: 100 }} onClick={() => setModoEdicion(true)}>
                Cambiar
              </Button>
              <Button
                color="danger"
                size="sm"
                style={{ minWidth: 100 }}
                onClick={async () => {
                  await actualizarProveedor(proveedor.id, {
                    cuenta_er_default: null,
                    tipo_contable_default: null,
                  });
                  toast.success(`Asociación olvidada para ${proveedor.nombre}`);
                  setProveedor({ ...proveedor, cuenta_er_default: null, tipo_contable_default: null });
                  setModoEdicion(true);
                }}
              >
                Olvidar
              </Button>
            </div>
          </div>
        )}

        {!esIgnorada && !cargando && !modoRapido && (
          <>
            {/* Proveedor identificado */}
            {proveedor && (
              <div
                style={{
                  fontSize: "0.78rem",
                  marginBottom: 12,
                  padding: "8px 12px",
                  background: "#d5f5e3",
                  borderRadius: 4,
                  color: "#27ae60",
                }}
              >
                Proveedor identificado: <strong>{proveedor.nombre}</strong> (ID {proveedor.id})
                {proveedor.cuenta_er_default && (
                  <span>
                    {" "}
                    — Cuenta habitual:{" "}
                    <strong>
                      {CUENTAS_ER.find((c) => c.value === proveedor.cuenta_er_default)?.label ||
                        proveedor.cuenta_er_default}
                    </strong>
                  </span>
                )}
              </div>
            )}

            {/* Factura proveedor existente */}
            {facturaProveedor && (
              <div
                style={{
                  fontSize: "0.78rem",
                  marginBottom: 12,
                  padding: "8px 12px",
                  background: "#e8f4fd",
                  borderRadius: 4,
                  color: "#2980b9",
                }}
              >
                Factura proveedor existente: #{facturaProveedor.numero_factura} —{" "}
                {formatMonto(facturaProveedor.monto_total)} — Estado: {facturaProveedor.estado}
              </div>
            )}

            {/* Selector Tipo Contable (arriba del todo) */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Tipo contable
              </label>
              <div style={{ display: "flex", gap: 16, fontSize: "0.82rem" }}>
                <label style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="tipoContable"
                    value="gasto"
                    checked={tipoContable === "gasto"}
                    onChange={() => {
                      setTipoContable("gasto");
                      setLineasActivo([]);
                    }}
                    style={{ marginRight: 4 }}
                  />
                  Gasto
                </label>
                <label style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="tipoContable"
                    value="activo"
                    checked={tipoContable === "activo"}
                    onChange={() => {
                      setTipoContable("activo");
                      if (lineasActivo.length === 0) agregarLinea();
                    }}
                    style={{ marginRight: 4 }}
                  />
                  Activo
                </label>
              </div>
            </div>

            {/* Selector Cuenta ER — solo para gastos */}
            {tipoContable === "gasto" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Cuenta del Estado de Resultados
                </label>
                <select
                  value={cuentaER}
                  onChange={(e) => setCuentaER(e.target.value)}
                  style={{
                    width: "100%",
                    height: 36,
                    fontSize: "0.82rem",
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="">— Seleccionar cuenta —</option>
                  {Object.entries(CUENTAS_POR_GRUPO).map(([grupo, cuentas]) => (
                    <optgroup key={grupo} label={grupo}>
                      {cuentas.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {/* ─── Sección Líneas de Activo (compacta) ──────── */}
            {tipoContable === "activo" && (
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "10px 12px",
                  marginBottom: 12,
                  background: "#fcfcfd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>Líneas de activo ({lineasActivo.length})</span>
                  <Button color="dark" outline size="sm" onClick={agregarLinea} style={{ fontSize: "0.75rem" }}>
                    + Agregar línea
                  </Button>
                </div>

                {/* Cabecera columnas */}
                {lineasActivo.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      fontSize: "0.68rem",
                      color: "#888",
                      fontWeight: 600,
                      padding: "0 2px 4px",
                    }}
                  >
                    <span style={{ width: 200, minWidth: 200 }}>Tipo *</span>
                    <span style={{ width: 70, minWidth: 70, textAlign: "center" }}>Cant. *</span>
                    <span style={{ width: 120, minWidth: 120 }}>Costo unit. *</span>
                    <span style={{ width: 180, minWidth: 180 }}>Destino</span>
                    <span style={{ width: 22 }} />
                  </div>
                )}

                {lineasActivo.map((linea, idx) => {
                  const destinoOpciones = getDestinoOpciones(linea.destino_tipo);
                  const expanded = !!lineasExpandidas[idx];
                  const vidaUtil = linea.tipo_activo ? getVidaUtilPorTipo(linea.tipo_activo) : null;

                  return (
                    <div key={idx} style={{ marginBottom: 4 }}>
                      {/* Fila principal compacta */}
                      <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.78rem" }}>
                        {/* Tipo activo */}
                        <select
                          value={linea.tipo_activo}
                          onChange={(e) => actualizarLinea(idx, "tipo_activo", e.target.value)}
                          style={{
                            width: 200,
                            minWidth: 200,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 4px",
                          }}
                        >
                          <option value="">— Tipo —</option>
                          {TIPOS_ACTIVO.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>

                        {/* Cantidad */}
                        <input
                          type="number"
                          min={1}
                          value={linea.cantidad}
                          onChange={(e) => actualizarLinea(idx, "cantidad", parseInt(e.target.value, 10) || 1)}
                          style={{
                            width: 70,
                            minWidth: 70,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 6px",
                            textAlign: "center",
                          }}
                        />

                        {/* Costo unitario */}
                        <input
                          type="number"
                          min={0}
                          value={linea.costo_unitario}
                          onChange={(e) => actualizarLinea(idx, "costo_unitario", parseFloat(e.target.value) || 0)}
                          style={{
                            width: 120,
                            minWidth: 120,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 6px",
                          }}
                        />

                        {/* Destino — selector combinado tipo + id */}
                        {!linea.destino_tipo || destinoOpciones.length === 0 ? (
                          <select
                            value={linea.destino_tipo}
                            onChange={(e) => actualizarLinea(idx, "destino_tipo", e.target.value)}
                            style={{
                              width: 180,
                              minWidth: 180,
                              height: 30,
                              fontSize: "0.76rem",
                              border: "1px solid #ced4da",
                              borderRadius: 3,
                              padding: "2px 4px",
                            }}
                          >
                            {DESTINO_TIPOS.map((d) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ width: 180, minWidth: 180, display: "flex", gap: 3 }}>
                            <select
                              value={linea.destino_id}
                              onChange={(e) => actualizarLinea(idx, "destino_id", Number(e.target.value) || "")}
                              style={{
                                flex: 1,
                                height: 30,
                                fontSize: "0.76rem",
                                border: "1px solid #ced4da",
                                borderRadius: 3,
                                padding: "2px 4px",
                              }}
                            >
                              <option value="">
                                {linea.destino_tipo === "edificio" ? "Edificio..." : "Proyecto..."}
                              </option>
                              {destinoOpciones.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => actualizarLinea(idx, "destino_tipo", "")}
                              title="Cambiar destino"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#999",
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                padding: "0 2px",
                              }}
                            >
                              &larr;
                            </button>
                          </div>
                        )}

                        {/* Botón eliminar */}
                        <button
                          onClick={() => eliminarLinea(idx)}
                          disabled={lineasActivo.length <= 1}
                          style={{
                            width: 22,
                            minWidth: 22,
                            background: "none",
                            border: "none",
                            color: lineasActivo.length > 1 ? "#e74c3c" : "#ccc",
                            cursor: lineasActivo.length > 1 ? "pointer" : "default",
                            fontSize: "1rem",
                            padding: 0,
                            lineHeight: 1,
                          }}
                          title="Eliminar línea"
                        >
                          &times;
                        </button>
                      </div>

                      {/* Link "ver más" + campos expandibles */}
                      <div style={{ paddingLeft: 2, marginTop: 2 }}>
                        <button
                          onClick={() => setLineasExpandidas((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#2980b9",
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            padding: 0,
                          }}
                        >
                          {expanded ? "ocultar" : "ver más"}
                          {vidaUtil && !expanded ? ` (${vidaUtil}m)` : ""}
                        </button>

                        {expanded && (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              marginTop: 4,
                              marginBottom: 2,
                              fontSize: "0.76rem",
                            }}
                          >
                            <div style={{ flex: 2 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>Descripción</label>
                              <input
                                type="text"
                                value={linea.descripcion}
                                onChange={(e) => actualizarLinea(idx, "descripcion", e.target.value)}
                                placeholder="Descripción del activo"
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #ced4da",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>N° Serie</label>
                              <input
                                type="text"
                                value={linea.numero_serie}
                                onChange={(e) => actualizarLinea(idx, "numero_serie", e.target.value)}
                                placeholder="Opcional"
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #ced4da",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                }}
                              />
                            </div>
                            <div style={{ width: 80 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>Vida útil</label>
                              <input
                                type="text"
                                readOnly
                                value={vidaUtil ? `${vidaUtil}m` : "—"}
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #e9ecef",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                  background: "#f5f5f5",
                                  color: "#888",
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Totalizador */}
                {lineasActivo.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: 4,
                      background: diferenciaOk ? "#d5f5e3" : "#fdecea",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      marginTop: 6,
                    }}
                  >
                    <span>Total: {formatMonto(totalLineas)}</span>
                    <span>Neto: {formatMonto(montoNeto)}</span>
                    <span style={{ color: diferenciaOk ? "#27ae60" : "#e74c3c" }}>
                      {diferenciaOk ? "Coincide" : `Dif: ${formatMonto(Math.abs(totalLineas - montoNeto))}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Recordar para proveedor */}
            {proveedor && (
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: "0.78rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={recordar}
                    onChange={(e) => setRecordar(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Recordar esta cuenta para <strong>{proveedor.nombre}</strong>
                  {cuentaLabel && <span> ({cuentaLabel})</span>}
                </label>
              </div>
            )}
          </>
        )}

        {/* Panel de confirmación */}
        {!esIgnorada && confirmando && (
          <div
            style={{
              marginTop: 16,
              padding: "14px 16px",
              background: "#eaf2f8",
              borderRadius: 6,
              border: "1px solid #aed6f1",
              fontSize: "0.82rem",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Confirmar asignación</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 28px" }}>
              <div>
                <strong>DTE Folio:</strong> {factura.folio} — {formatMonto(factura.monto_total)}
              </div>
              <div>
                <strong>Proveedor:</strong> {proveedor?.nombre || factura.razon_social_emisor}
              </div>
              <div>
                <strong>Cuenta ER:</strong> {cuentaLabel || cuentaER}
              </div>
              <div>
                <strong>Tipo:</strong> {tipoContable}
              </div>
            </div>
            {/* Resumen líneas de activo */}
            {tipoContable === "activo" && lineasActivo.length > 0 && (
              <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#555" }}>
                <strong>Líneas de activo:</strong> {lineasActivo.length} línea{lineasActivo.length > 1 ? "s" : ""} —
                Total: {formatMonto(totalLineas)}
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {lineasActivo.map((l, i) => {
                    const tipoLabel = TIPOS_ACTIVO.find((t) => t.value === l.tipo_activo)?.label || l.tipo_activo;
                    return (
                      <li key={i}>
                        {l.cantidad}x {tipoLabel}
                        {l.descripcion ? ` — ${l.descripcion}` : ""}
                        {l.destino_tipo === "edificio"
                          ? ` → ${edificios.find((e) => e.id === l.destino_id)?.edificio || "Edificio"}`
                          : l.destino_tipo === "carteleria"
                            ? ` → ${proyectosCarteleria.find((p) => p.id === l.destino_id)?.nombre_proyecto || "Cartelería"}`
                            : " → Sin asignar"}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                Pago estimado <span style={{ color: "#e74c3c" }}>*</span>
              </label>
              <input
                type="date"
                value={plazopago}
                onChange={(e) => setPlazopago(e.target.value)}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: `1px solid ${!plazopago ? "#e74c3c" : "#aed6f1"}`,
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
              <Button color="secondary" outline size="sm" onClick={() => setConfirmando(false)}>
                Volver
              </Button>
              <Button color="dark" size="sm" onClick={handleAsignar} disabled={guardando}>
                {guardando ? "Guardando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {esIgnorada ? (
          <>
            <Button color="secondary" size="sm" onClick={toggle}>
              Cerrar
            </Button>
            <Button color="warning" size="sm" onClick={handleReactivar} disabled={guardando}>
              {guardando ? "..." : "Reactivar"}
            </Button>
          </>
        ) : confirmando || modoRapido ? (
          <Button
            color="secondary"
            outline
            size="sm"
            onClick={confirmando ? toggle : handleIgnorar}
            disabled={guardando}
          >
            {confirmando ? "Cerrar" : "Descartar"}
          </Button>
        ) : (
          <>
            <Button color="secondary" outline size="sm" onClick={handleIgnorar} disabled={guardando}>
              Descartar
            </Button>
            <Button
              color="dark"
              size="sm"
              onClick={() => setConfirmando(true)}
              disabled={guardando || !tipoContable || (tipoContable === "gasto" && !cuentaER)}
            >
              Asignar
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AsignarRecibidaModal;
